import { useTranslation } from '../i18n';
import React, { useEffect, useRef, useState, useCallback } from 'react';

/**
 * CaptchaLa (captcha.la) 验证码组件，支持两类 product：
 *
 * - 内联直出（无按钮/无 bar）：product='embed' | 'float' → 用 inst.appendTo(容器) 直接渲染验证组件。
 *     <CaptchaLa product="embed" action="login" onVerify={(t) => retry(t)} />
 * - 绑定按钮点击触发：product='bind' | 'popup' → 用 inst.bindTo(按钮选择器)。
 *     <CaptchaLa product="bind" targetSelector="#login-btn" action="login" onVerify={...} />
 *
 * 通过后 onSuccess(res.token) 回调，把 pt_ 开头的 token 交给 onVerify。
 * 经核对真实 SDK：init/onSuccess/onError/bindTo/appendTo/appKey/product/action 均为真实 API。
 */
const APPEND_PRODUCTS = ['embed', 'float'];

const LOADER_SRC = 'https://cdn.captcha-cdn.net/captchala-loader.js';
const APP_KEY = process.env.REACT_APP_CAPTCHALA_APP_KEY || 'app_b6f0231fb6e4bf8b33ae51238a5b98a5';

// loader 脚本全局单例
let loaderPromise = null;
function loadLoaderScript() {
    if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
    if (window.loadCaptchala || window.Captchala) return Promise.resolve();
    if (loaderPromise) return loaderPromise;
    loaderPromise = new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${LOADER_SRC}"]`);
        if (existing) {
            existing.addEventListener('load', () => resolve());
            existing.addEventListener('error', () => reject(new Error('captchala loader failed')));
            if (window.loadCaptchala || window.Captchala) resolve();
            return;
        }
        const s = document.createElement('script');
        s.src = LOADER_SRC;
        s.async = true;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error('captchala loader failed'));
        document.head.appendChild(s);
    });
    return loaderPromise;
}

// 等 SDK 就绪（loader 会再异步加载主 SDK）
function whenReady() {
    return new Promise((resolve, reject) => {
        if (window.Captchala && typeof window.Captchala.init === 'function') return resolve();
        if (typeof window.loadCaptchala === 'function') {
            let done = false;
            window.loadCaptchala(() => { if (!done) { done = true; resolve(); } });
            // 兜底轮询，避免回调不触发
            let tries = 0;
            const timer = setInterval(() => {
                tries += 1;
                if (window.Captchala && typeof window.Captchala.init === 'function') {
                    clearInterval(timer);
                    if (!done) { done = true; resolve(); }
                } else if (tries > 100) { // ~10s
                    clearInterval(timer);
                    if (!done) { done = true; reject(new Error('captchala SDK timeout')); }
                }
            }, 100);
            return;
        }
        reject(new Error('loadCaptchala missing'));
    });
}

const CaptchaLa = ({ action = 'login', product = 'embed', lang = 'auto', targetSelector, onVerify, onError, style }) => {
    const { t } = useTranslation();
    const instanceRef = useRef(null);
    const containerRef = useRef(null);
    const [status, setStatus] = useState('loading'); // loading | ready | error

    const handleError = useCallback((err) => {
        setStatus('error');
        // eslint-disable-next-line no-console
        console.error('[CaptchaLa] error:', err && (err.message || err));
        if (onError) onError(err);
    }, [onError]);

    useEffect(() => {
        let cancelled = false;

        loadLoaderScript()
            .then(() => whenReady())
            .then(() => {
                if (cancelled) return;
                if (!window.Captchala || typeof window.Captchala.init !== 'function') {
                    throw new Error('Captchala.init missing');
                }
                const inst = window.Captchala.init({ appKey: APP_KEY, product, action, lang });
                instanceRef.current = inst;

                if (typeof inst.onSuccess === 'function') {
                    inst.onSuccess((res) => {
                        if (cancelled) return;
                        const token = res && res.token;
                        if (token && onVerify) onVerify(token);
                    });
                }
                if (typeof inst.onError === 'function') {
                    inst.onError((err) => { if (!cancelled) handleError(err); });
                }

                // embed/float：内联直出，appendTo 到容器；popup/bind：绑定到按钮
                if (APPEND_PRODUCTS.includes(product)) {
                    if (containerRef.current && typeof inst.appendTo === 'function') {
                        inst.appendTo(containerRef.current);
                    } else if (containerRef.current && typeof inst.bindTo === 'function') {
                        inst.bindTo(containerRef.current);
                    }
                } else {
                    const target = targetSelector || containerRef.current;
                    if (target && typeof inst.bindTo === 'function') {
                        inst.bindTo(target);
                    }
                }
                setStatus('ready');
            })
            .catch((err) => { if (!cancelled) handleError(err); });

        return () => {
            cancelled = true;
            try {
                const inst = instanceRef.current;
                if (inst && typeof inst.destroy === 'function') inst.destroy();
            } catch (e) { /* noop */ }
            instanceRef.current = null;
        };
    }, [action, product, lang, targetSelector, onVerify, handleError]);

    return (
        <div style={style}>
            <div ref={containerRef} />
            {status === 'error' ? (
                <div style={{ color: '#cf1322', fontSize: 12, marginTop: 4 }}>
                    {t('Failed to load CAPTCHA. Please refresh the page or try again later.')}
                </div>
            ) : null}
        </div>
    );
};

export default CaptchaLa;
